import React from 'react';
import { useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { Flex, Box, Text } from 'rebass/styled-components';
import { Loader, Checkbox } from '@getro/rombo';
import { fieldRendererMap } from 'components/molecules/signupFormPreview';
import usePageTitle from 'hooks/usePageTitle';
import { FormHeader, NotFoundMessage, PoweredByFooter, SuccessMessage } from './components';
import { usePublicSignupForm } from './usePublicSignupForm';

const PublicSignupForm = () => {
  const { slug, networkId } = useParams();
  const {
    formData,
    networkName,
    networkLogo,
    isLoading,
    isSubmitting,
    isSubmitted,
    isNotFound,
    submitError,
    initialValues,
    validationSchema,
    onSubmit,
  } = usePublicSignupForm({ slug, networkId });

  usePageTitle(formData?.title);

  if (isLoading) {
    return (
      <Flex
        sx={{
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          bg: 'white',
        }}
      >
        <Loader />
      </Flex>
    );
  }

  if (isNotFound) {
    return (
      <>
        <NotFoundMessage />
        <PoweredByFooter />
      </>
    );
  }

  if (isSubmitted) {
    return (
      <Flex
        sx={{
          minHeight: '100vh',
          bg: 'white',
          flexDirection: 'column',
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: '80px',
            width: '100%',
            maxWidth: '520px',
            margin: '0 auto',
            py: '68px',
            px: '24px',
          }}
        >
          <FormHeader
            title={formData?.title || 'Talent Community'}
            description={formData?.description}
            networkName={networkName}
            networkLogo={networkLogo}
          />

          <SuccessMessage title={formData?.successMessageTitle} description={formData?.successMessageDescription} />
        </Flex>
        <PoweredByFooter />
      </Flex>
    );
  }

  return (
    <Flex
      sx={{
        minHeight: '100vh',
        bg: 'white',
        flexDirection: 'column',
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnBlur
        validateOnChange={false}
      >
        {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
          <Form
            style={{
              width: '100%',
              maxWidth: '520px',
              margin: '0 auto',
              padding: '68px 24px',
            }}
          >
            <Flex sx={{ flexDirection: 'column', gap: '48px' }}>
              <FormHeader
                title={formData?.title || 'Talent Community'}
                description={formData?.description}
                networkName={networkName}
                networkLogo={networkLogo}
              />

              <Flex sx={{ flexDirection: 'column', gap: '24px' }}>
                {formData?.fields?.map((field) => {
                  const FieldRenderer = fieldRendererMap[field.type];
                  const fieldId = field.id?.toString() || field.tempId;

                  if (!fieldId) {
                    return null;
                  }

                  return FieldRenderer ? (
                    <FieldRenderer
                      key={fieldId}
                      field={field}
                      value={values[fieldId]}
                      onChange={(e) => {
                        const value = e?.target?.value !== undefined ? e.target.value : e;
                        setFieldValue(fieldId, value);
                      }}
                      onBlur={() => setFieldTouched(fieldId, true, true)}
                      error={touched[fieldId] ? errors[fieldId] : undefined}
                      disabled={isSubmitting}
                    />
                  ) : null;
                })}

                <Flex sx={{ flexDirection: 'column', gap: '4px' }}>
                  <Flex
                    as="label"
                    sx={{
                      gap: '8px',
                      alignItems: 'flex-start',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <Box sx={{ flexShrink: 0, mt: '2px' }}>
                      <Checkbox
                        checked={values.termsAccepted}
                        onChange={(e) => setFieldValue('termsAccepted', e.target.checked)}
                        disabled={isSubmitting}
                      />
                    </Box>
                    <Text
                      sx={{
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: 'text.main',
                      }}
                    >
                      I have read and I do accept the{' '}
                      <Text
                        as="a"
                        href="https://www.getro.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          color: 'text.main',
                          textDecoration: 'underline',
                          fontWeight: '500',
                        }}
                      >
                        terms of use
                      </Text>{' '}
                      and{' '}
                      <Text
                        as="a"
                        href="https://www.getro.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          color: 'text.main',
                          textDecoration: 'underline',
                          fontWeight: '500',
                        }}
                      >
                        privacy policy
                      </Text>
                      .
                    </Text>
                  </Flex>
                  {touched.termsAccepted && errors.termsAccepted && (
                    <Text sx={{ fontSize: '14px', color: 'danger', ml: '28px' }}>{errors.termsAccepted}</Text>
                  )}
                </Flex>
                {submitError && (
                  <Box
                    sx={{
                      p: '12px',
                      bg: 'danger.50',
                      borderRadius: '4px',
                      border: '1px solid',
                      borderColor: 'danger',
                    }}
                  >
                    <Text sx={{ fontSize: '14px', color: 'danger' }}>{submitError}</Text>
                  </Box>
                )}
                <Box
                  as="button"
                  type="submit"
                  disabled={isSubmitting}
                  sx={{
                    width: '100%',
                    height: '40px',
                    bg: 'purple.400',
                    borderRadius: '4px',
                    border: 'none',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    lineHeight: '20px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isSubmitting ? 'Submitting...' : formData?.submitButtonText || 'Join now'}
                </Box>
              </Flex>
            </Flex>
          </Form>
        )}
      </Formik>
      <PoweredByFooter />
    </Flex>
  );
};

export default PublicSignupForm;
